import { parseAllowedOtcInterviewRedirectPath } from "../url/otcRedirectAllowlist";

// 要指導の画面間で引き継ぐ一時状態。ID・日時・URL だけを保存し、医療情報・個人情報は保存しない。
// EC へ戻る URL は EC の取引トークンを含むため、予約完了の画面で使ったら破棄する
const ENTRY_KEY = "otc-guidance-entry";
const CALLBACK_URL_KEY_PREFIX = "otc-guidance-callback-url:";
const COUNSELING_ID_KEY_PREFIX = "otc-guidance-counseling-id:";
const CLEAR_TARGET_KEY_PREFIX = "otc-guidance-";
// 戻り先は問診から会員情報の登録に進んだ直後だけ使う。古い値で通常の会員情報登録を置き換えない
const ENTRY_TTL_MS = 60 * 60 * 1000;

type OtcGuidanceEntry = { uid: string; redirectUrl: string; savedAt: number };

const getItem = (key: string) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setItem = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // 保存できない環境では引き継ぎを諦め、URL のクエリだけで進める
  }
};

const removeItem = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // 削除できない環境では何もしない
  }
};

const isOtcGuidanceEntry = (value: unknown): value is OtcGuidanceEntry =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as OtcGuidanceEntry).uid === "string" &&
  typeof (value as OtcGuidanceEntry).redirectUrl === "string" &&
  typeof (value as OtcGuidanceEntry).savedAt === "number";

/** 会員情報登録へ進む前に、戻り先の問診画面をログイン中の患者に結び付けて保存する */
export const saveOtcGuidanceEntry = ({ uid, redirectUrl }: Omit<OtcGuidanceEntry, "savedAt">) => {
  setItem(ENTRY_KEY, JSON.stringify({ uid, redirectUrl, savedAt: Date.now() }));
};

/**
 * 会員情報登録の画面をリロードした場合などに、戻り先の問診画面を復元する。
 * 別の患者の保存値や、問診画面以外を指す値は破棄する
 */
export const loadOtcGuidanceEntryRedirectUrl = (uid?: string): string | undefined => {
  const raw = getItem(ENTRY_KEY);
  if (!raw || !uid) return undefined;

  try {
    const entry: unknown = JSON.parse(raw);
    const isValidEntry = isOtcGuidanceEntry(entry) && entry.uid === uid && Date.now() - entry.savedAt <= ENTRY_TTL_MS;
    const redirectUrl = isValidEntry ? parseAllowedOtcInterviewRedirectPath(entry.redirectUrl) : undefined;
    if (!redirectUrl) removeItem(ENTRY_KEY);
    return redirectUrl;
  } catch {
    removeItem(ENTRY_KEY);
    return undefined;
  }
};

export const clearOtcGuidanceEntry = () => removeItem(ENTRY_KEY);

/** 回答送信から予約完了までの間、EC へ戻る URL を案件ごとに保持する(リロード時の復元用) */
export const saveOtcGuidanceCallbackUrl = (screeningId: string, callbackUrl: string) =>
  setItem(`${CALLBACK_URL_KEY_PREFIX}${screeningId}`, callbackUrl);

export const loadOtcGuidanceCallbackUrl = (screeningId: string) =>
  getItem(`${CALLBACK_URL_KEY_PREFIX}${screeningId}`) ?? undefined;

export const clearOtcGuidanceCallbackUrl = (screeningId: string) =>
  removeItem(`${CALLBACK_URL_KEY_PREFIX}${screeningId}`);

export type StoredOtcGuidanceCounseling = { counselingId: number; reservedAt?: string };

/** 予約を作成してから案件への紐付けが終わるまで、予約 ID と確定日時を保持する(紐付けだけを再試行するため) */
export const saveOtcGuidanceCounseling = (
  screeningId: string,
  { counselingId, reservedAt }: StoredOtcGuidanceCounseling
) => setItem(`${COUNSELING_ID_KEY_PREFIX}${screeningId}`, JSON.stringify({ counselingId, reservedAt }));

export const loadOtcGuidanceCounseling = (screeningId: string): StoredOtcGuidanceCounseling | undefined => {
  const raw = getItem(`${COUNSELING_ID_KEY_PREFIX}${screeningId}`);
  if (!raw) return undefined;

  try {
    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== "object" || stored === null) return undefined;

    const { counselingId, reservedAt } = stored as Partial<StoredOtcGuidanceCounseling>;
    if (typeof counselingId !== "number" || !Number.isInteger(counselingId) || counselingId <= 0) return undefined;
    return { counselingId, reservedAt: typeof reservedAt === "string" ? reservedAt : undefined };
  } catch {
    return undefined;
  }
};

export const clearOtcGuidanceCounseling = (screeningId: string) =>
  removeItem(`${COUNSELING_ID_KEY_PREFIX}${screeningId}`);

/** ログアウトなど認証主体が変わるときに、要指導の一時状態をすべて破棄する */
export const clearOtcGuidanceSessionStorage = () => {
  try {
    const keys = Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index));
    keys
      .filter((key): key is string => !!key && key.startsWith(CLEAR_TARGET_KEY_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // 削除できない環境では何もしない
  }
};
